// Drugs › Measured ΔFC in the Inspector (ba-web): a cortical region picked on the cortex shows the condition's change
// over it and the Schaefer parcels inside it, each a button that puts the parcel under inspection in the scene panel's
// Parcels section (features/Dfc.tsx). Nothing in other views: `dfcActive` gates the rows, and a structure the HD
// cortex's DKT layer hasn't (a subcortical nucleus, a vessel) gets none. The numbers are features/dfcInspector.ts's.

import { useMemo } from 'react';
import { useAtlas } from '../app/store.ts';
import { dktCrosswalk } from '../app/sims.ts';
import { useLoaded } from '../ui/hooks.ts';
import { dfcActive } from './Dfc.tsx';
import { conditionLabel, dzText, parcelName, useDfc } from './dfc.ts';
import { regionDfc, shareText } from './dfcInspector.ts';
import './web.css';

export function DfcRows({ structure }: { structure: string }) {
  const active = useAtlas(dfcActive);
  const data = useDfc((s) => s.data);
  const id = useDfc((s) => s.condition);
  const parcel = useDfc((s) => s.parcel);
  const setParcel = useDfc((s) => s.setParcel);
  // The crosswalk pharma's region overlay reads (one cache); null until the pack's layers are in.
  const cw = useLoaded(() => (active ? dktCrosswalk() : null), [active]);
  const c = data ? data.condition(id) : null;
  const region = useMemo(
    () => (active && data && c && cw.value ? regionDfc(cw.value, structure, data.nodes, data.strength(c.index), (i) => data.measured(c.index, i)) : null),
    [active, data, c, cw.value, structure],
  );
  if (!active || !data || !c || !region) return null;
  const label = conditionLabel(c);
  const all = region.parcels.length + region.slivers;
  return (
    <dl className="insp-facts insp-live-rows insp-dfc-rows" aria-label={`Measured ΔFC, ${label}`}>
      <div className="insp-live-row">
        <dt>Change</dt>
        <dd className="insp-wide num" title={`${label}: the vertex-weighted mean change of the measured Schaefer parcels inside this region, on the cortex the viewport draws`}>
          {region.mean !== null ? `${dzText(region.mean)} Δz over ${region.measured} of ${all} parcels` : `No measurement in its ${all} parcels`}
        </dd>
      </div>
      <div className="insp-live-row insp-dfc-parcels">
        <dt>Parcels</dt>
        <dd className="insp-wide">
          <ul className="dfc-ranked insp-dfc-list" aria-label="Schaefer parcels in this region, by share of its vertices">
            {region.parcels.map((p) => (
              <li key={p.node.index}>
                <button
                  type="button"
                  className={`dfc-parcel ${parcel === p.node.index ? 'on' : ''}`}
                  aria-pressed={parcel === p.node.index}
                  title={`${p.vertices.toLocaleString()} of the region’s ${region.vertices.toLocaleString()} vertices`}
                  onClick={() => setParcel(parcel === p.node.index ? null : p.node.index)}
                >
                  <span className="dfc-parcel-name">{parcelName(p.node)}</span>
                  <span className="insp-dfc-share num muted">{shareText(p.share)}</span>
                  <span className={`dfc-dz num ${p.dz === null ? 'muted' : p.dz < 0 ? 'neg' : 'pos'}`}>{p.dz === null ? '—' : dzText(p.dz)}</span>
                </button>
              </li>
            ))}
          </ul>
          {region.slivers ? <p className="ba-type-caption muted insp-dfc-slivers">{`${region.slivers} more under 1 % of its vertices`}</p> : null}
        </dd>
      </div>
    </dl>
  );
}
